import axios from 'axios';
import { getApiBaseUrl } from '../utils/config';

// 动态获取API基础URL
const API_BASE_URL = getApiBaseUrl();

// 创建axios实例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 增加超时时间以支持文件上传
  headers: {
    'Content-Type': 'application/json',
  }
});

// Token管理函数 - 正确解码token
const getToken = () => {
  const TOKEN_KEY = 'access_token';
  const encodedToken = localStorage.getItem(TOKEN_KEY);
  if (!encodedToken) return null;
  
  try {
    const decoded = atob(encodedToken);
    const parts = decoded.split('|');
    if (parts.length >= 2) {
      const token = parts[0];
      const timestamp = parseInt(parts[1]);
      
      // 检查token是否过期（7天本地存储限制）
      const maxAge = 7 * 24 * 60 * 60 * 1000; // 7天
      if (Date.now() - timestamp > maxAge) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('current_user');
        return null;
      }
      
      return token;
    }
  } catch (error) {
    console.error('Token解码失败:', error);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('current_user');
  }
  
  return null;
};

// 请求拦截器：自动添加认证token
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：处理认证错误
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const errorMessage = error.response?.data?.detail || '';
      
      // 只有在明确的token过期或无效时才清除认证信息
      if (errorMessage.includes('过期') || 
          errorMessage.includes('无效') || 
          errorMessage.includes('expired') || 
          errorMessage.includes('invalid')) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('current_user');
        window.dispatchEvent(new CustomEvent('auth-logout', { 
          detail: { reason: 'token_expired' } 
        }));
      }
    }
    return Promise.reject(error);
  }
);

// ====================
// 系统API
// ====================

// 已删除废弃的healthCheck和getSystemStatus函数
// 新版本不需要这些系统检查功能

// ====================
// 任务管理API
// ====================

// 创建新任务
export const createTask = async (taskData) => {
  try {
    const response = await apiClient.post('/api/v1/tasks/create', taskData);
    return response.data;
  } catch (error) {
    throw new Error(`创建任务失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 上传文件到指定任务
export const uploadFileToTask = async (taskId, file, onProgress = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    
    // 如果提供了进度回调，添加上传进度跟踪
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      };
    }
    
    const response = await apiClient.post(`/api/v1/tasks/${taskId}/upload`, formData, config);
    return response.data;
  } catch (error) {
    throw new Error(`文件上传失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 开始处理任务
export const startTaskProcessing = async (taskId) => {
  try {
    const response = await apiClient.post(`/api/v1/tasks/${taskId}/start`);
    return response.data;
  } catch (error) {
    throw new Error(`开始处理失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 获取任务状态
export const getTaskStatus = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/tasks/${taskId}/status`);
    return response.data;
  } catch (error) {
    throw new Error(`获取任务状态失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 下载任务结果Excel
export const downloadTaskResult = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/tasks/${taskId}/download`, {
      responseType: 'blob', // 重要：指定响应类型为blob
    });
    
    // 创建下载链接
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // 从响应头获取文件名，如果没有则使用默认名称
    const contentDisposition = response.headers['content-disposition'];
    let filename = `task_${taskId}_result.xlsx`;
    if (contentDisposition) {
      // 修复文件名解析，支持带引号和不带引号的情况
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, ''); // 移除引号
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true, filename };
  } catch (error) {
    throw new Error(`下载失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 获取用户任务列表
export const getUserTasks = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/v1/tasks', { params });
    return response.data;
  } catch (error) {
    throw new Error(`获取任务列表失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 获取任务统计信息
export const getTaskStatistics = async () => {
  try {
    const response = await apiClient.get('/api/v1/tasks/statistics');
    return response.data;
  } catch (error) {
    throw new Error(`获取任务统计失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 删除任务
export const deleteTask = async (taskId) => {
  try {
    const response = await apiClient.delete(`/api/v1/tasks/${taskId}`);
    return response.data;
  } catch (error) {
    throw new Error(`删除任务失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 获取任务文件列表
export const getTaskFiles = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/tasks/${taskId}/files`);
    return response.data;
  } catch (error) {
    throw new Error(`获取文件列表失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 获取任务失败的文件列表
export const getFailedFiles = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/tasks/${taskId}/files`);
    const files = response.data.files || [];
    // 过滤出失败的文件
    const failedFiles = files.filter(file => file.processing_status === 'failed');
    return {
      task_id: taskId,
      failed_files: failedFiles,
      total_failed: failedFiles.length
    };
  } catch (error) {
    throw new Error(`获取失败文件列表失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 获取处理器状态
export const getProcessingStatus = async () => {
  try {
    const response = await apiClient.get('/api/v1/tasks/processing-status');
    return response.data;
  } catch (error) {
    throw new Error(`获取处理器状态失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 批量下载任务结果Excel
export const batchDownloadTaskResults = async (taskIds) => {
  try {
    const response = await apiClient.post('/api/v1/tasks/batch-download', {
      task_ids: taskIds
    }, {
      responseType: 'blob', // 重要：指定响应类型为blob
    });
    
    // 创建下载链接
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // 从响应头获取文件名，如果没有则使用默认名称
    const contentDisposition = response.headers['content-disposition'];
    let filename = `聚合下载_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
    if (contentDisposition) {
      // 修复文件名解析，支持带引号和不带引号的情况
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, ''); // 移除引号
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    // 获取临时文件路径用于清理
    const tempFilePath = response.headers['x-file-path'];
    
    return { 
      success: true, 
      filename,
      tempFilePath
    };
  } catch (error) {
    throw new Error(`批量下载失败: ${error.response?.data?.detail || error.message}`);
  }
};

// 清理临时文件
export const cleanupTempFile = async (filePath) => {
  try {
    const response = await apiClient.post('/api/v1/tasks/cleanup-temp-file', {
      file_path: filePath
    });
    return response.data;
  } catch (error) {
    throw new Error(`清理临时文件失败: ${error.response?.data?.detail || error.message}`);
  }
};

// ====================
// 高级API - 完整的文件处理流程
// ====================

// 完整的文件处理流程：创建任务 -> 上传文件 -> 开始处理
export const processFilesWithNewAPI = async (files, options = {}, onProgress = null) => {
  try {
    const {
      taskName = null,
      docType = "lab_routine",
      patientFields = null,
      projectFieldName = "项目名称",
      resultFieldName = "结果"
    } = options;
    
    // 第一步：创建任务
    if (onProgress) onProgress({ step: 'creating_task', progress: 0, message: '创建任务中...' });
    
    const taskData = {
      task_name: taskName,
      doc_type: docType,
      patient_fields: patientFields,
      project_field_name: projectFieldName,
      result_field_name: resultFieldName
    };
    
    const taskResponse = await createTask(taskData);
    const taskId = taskResponse.task_id;
    
    // 第二步：上传文件
    if (onProgress) onProgress({ step: 'uploading_files', progress: 10, message: '上传文件中...' });
    
    const uploadResults = await uploadFilesToTask(taskId, files, (uploadProgress) => {
      const overallProgress = 10 + (uploadProgress * 0.6); // 上传占总进度的60%
      if (onProgress) onProgress({ 
        step: 'uploading_files', 
        progress: overallProgress, 
        message: `上传中... ${Math.round(uploadProgress)}%` 
      });
    });
    
    // 第三步：开始处理
    if (onProgress) onProgress({ step: 'starting_processing', progress: 70, message: '开始处理...' });
    
    const processingResponse = await startTaskProcessing(taskId);
    
    if (onProgress) onProgress({ step: 'processing_started', progress: 75, message: '处理已开始' });
    
    return {
      task_id: taskId,
      status: 'processing',
      upload_results: uploadResults,
      processing_response: processingResponse,
      message: `任务 ${taskId} 已创建并开始处理`
    };
    
  } catch (error) {
    throw new Error(`文件处理流程失败: ${error.message}`);
  }
};

// 批量上传文件到任务
export const uploadFilesToTask = async (taskId, files, onProgress = null) => {
  try {
    const results = [];
    const totalFiles = files.length;
    
    for (let i = 0; i < totalFiles; i++) {
      const file = files[i];
      const fileProgress = (i / totalFiles) * 100;
      
      if (onProgress) {
        onProgress(fileProgress);
      }
      
      const uploadResult = await uploadFileToTask(taskId, file, (individualProgress) => {
        const overallProgress = fileProgress + (individualProgress / totalFiles);
        if (onProgress) onProgress(overallProgress);
      });
      
      results.push({
        file_name: file.name,
        file_size: file.size,
        upload_result: uploadResult
      });
    }
    
    if (onProgress) onProgress(100);
    
    return {
      total_files: totalFiles,
      uploaded_files: results.length,
      results: results
    };
    
  } catch (error) {
    throw new Error(`批量上传失败: ${error.message}`);
  }
};

// 轮询任务状态直到完成
export const pollTaskStatus = async (taskId, onStatusUpdate = null, pollInterval = 3000) => {
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const status = await getTaskStatus(taskId);
        
        if (onStatusUpdate) {
          onStatusUpdate(status);
        }
        
        // 检查任务是否完成
        if (status.status === 'completed') {
          resolve(status);
          return;
        }
        
        if (status.status === 'failed') {
          reject(new Error(status.error_message || '任务处理失败'));
          return;
        }
        
        // 如果任务仍在处理中，继续轮询
        if (status.status === 'processing' || status.status === 'pending') {
          setTimeout(poll, pollInterval);
        } else {
          reject(new Error(`未知的任务状态: ${status.status}`));
        }
        
      } catch (error) {
        reject(error);
      }
    };
    
    // 开始轮询
    poll();
  });
};

// ====================
// 旧版本兼容性说明
// ====================

// 注意：旧版processDocument API已被完全移除
// 请使用新的任务管理API流程：createTask -> uploadFileToTask -> startTaskProcessing -> pollTaskStatus -> downloadTaskResult

// ====================
// 导出
// ====================

// 导出API客户端
export default apiClient;

// 导出动态获取的API基础URL
export { getApiBaseUrl } from '../utils/config';

// 初始化API服务
export const initializeApiService = () => {
  console.log('任务管理API服务已初始化');
  console.log(`API基础URL: ${API_BASE_URL}`);
}; 